import dayjs from "dayjs";
import { useEffect } from "react";
import { useRef } from "react";
import { constructDucFlowBasedFromState, constructStepLabelsFromState } from "./components/DucFlows";
import { steps } from "./DUCProcess";


const UseGetStepHookDucProcess = (data: any, setDUCInfo: Function,  setCurStep: Function,
    setExpiryCheck: Function, setStepLabels: Function, expiryCheck: any, DUCInfo: any,
    formType: any, userName: string, isGroup: boolean, isEdit: boolean, isAddSub: boolean, isSub: boolean, currentStep: number, beDucStatus?: string) => {
    // @ts-ignore
    const attestationEnabled = window.env.attestationEnabled;
    const didInitializeStepRef = useRef(false)
    //This hook is to get the correct step for the duc process based off of the current state of the application
    useEffect(() => {
        if (!data) return;

        // 1) Determine renewal context using THREE sources (priority order):
        //    a) server-provided type === 'Renewal'
        //    b) explicit UI signal via localStorage 'forceRenewal' set on Renewal button click
        //    c) client computation (expiry window, BE DucExpired, etc.)
        const serverSaysRenewal = data?.type === 'Renewal'
        const forceRenewalFlag = localStorage.getItem('forceRenewal') === 'true'

        // Client-computed renewal (3-month window based on status/expiry); subs excluded here
        let computedRenewal = !!expiryCheck;
        if (data?.status === 'DucExpired') {
            computedRenewal = true;
        } else if (beDucStatus === 'DucExpired') {
            // Backend override: treat BE DucExpired exactly as renewal context
            computedRenewal = true;
        } else if (data?.status === 'Approved' && !isSub) {
            const curDate = new Date();
            curDate.setMonth(curDate.getMonth() + 3);
            const expiryDay = dayjs(new Date(data?.expiresOn));
            if (expiryDay.isBefore(curDate, 'day')) {
                computedRenewal = true;
            }
        }

        // Combined flag with explicit override
        const isRenewal = !!serverSaysRenewal || !!forceRenewalFlag || !!computedRenewal;
        // Clear the one-shot flag immediately to avoid leaking renewal intent across navigations
        if (forceRenewalFlag) {
            localStorage.removeItem('forceRenewal')
        }

        // Build a single flow from the combined flag
        const groupFlow = isGroup || isAddSub
        const flow = constructDucFlowBasedFromState(isRenewal, attestationEnabled, groupFlow);

        // 2) Compute and set the current step using the same flow
        // IMPORTANT: Only move the step FORWARD during automatic initialization.
        // Never push the step backwards once the user has advanced via Back/Continue,
        // to avoid the "flicker" where the UI jumps back after clicking Continue.
        const forwardInit = (target: number) => {
            // Only auto-advance once during initial mount when step is at default (1 or unset).
            // This prevents sending users back to Sub-applicants after they navigate back to step 1.
            if (!didInitializeStepRef.current && (!currentStep || currentStep === 1) && target > 0) {
                setCurStep(target)
                didInitializeStepRef.current = true
            }
        }

        // One-shot override: when user clicked "View Certification" and BE has Approved, land on Review regardless of renewal
        const viewCertification = localStorage.getItem('viewCertification') === 'true'
        if (viewCertification && data?.status === 'Approved') {
            localStorage.removeItem('viewCertification')
            forwardInit(flow.indexOf(steps.REVIEW) + 1)
        } else if (isEdit) {
            if (isAddSub) {
                const subRecipientsStep = flow.indexOf(steps.SUBRECIPIENTS) + 1
                forwardInit(subRecipientsStep)
            } else {
                // In edit mode, always start at the first step (Recipients/Access Request).
                // Only initialize if the user hasn't advanced yet to avoid overriding Back/Continue.
                const recipientsStep = flow.indexOf(steps.RECIPIENTS) + 1;
                forwardInit(recipientsStep)
            }
        } else if (data?.status === 'Submitted') {
            forwardInit(flow.indexOf(steps.AUTHORIZEDOFFICIAL) + 1)
        } else if (data?.status === 'InProgress') {
            forwardInit(flow.indexOf(steps.REVIEW) + 1)
        } else if (data?.status === 'Approved') {
            if (isSub) {
                forwardInit(flow.indexOf(steps.REVIEW) + 1)
            } else {
                // If we're NOT in renewal context, go to Review
                if (!isRenewal) {
                    forwardInit(flow.indexOf(steps.REVIEW) + 1)
                }
            }
        } else if (data?.status === 'DucExpired') {
            forwardInit(flow.indexOf(steps.RECIPIENTS) + 1)
        } else if (data?.status === 'Declined') {
            if (data?.declined_by === 'EnvelopeExpiry') {
                // If declined due to envelope expiry, show all steps up to Review
                forwardInit(flow.indexOf(steps.REVIEW) + 1)
            } else {
                // For other decline reasons, default behavior
                forwardInit(flow.indexOf(steps.RECIPIENTS) + 1)
            }
        }

        // 3) Sync external expiry flag without causing loops (reflects combined renewal state)
        if (isRenewal && !expiryCheck) {
            setExpiryCheck(true);
        }

        // 4) Update DUCInfo type and step labels based on this flow (preserve server Renewal)
        setDUCInfo((prev: any) => ({
            ...prev,
            ducType: formType,
            type: isRenewal ? 'Renewal' : 'New',
            userName: userName,
            isGroup: groupFlow,
        }));
        setStepLabels(constructStepLabelsFromState(flow));
    }, [data, expiryCheck, attestationEnabled, isGroup, isEdit, isAddSub, isSub, formType, userName, currentStep])

}

export default UseGetStepHookDucProcess
